import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import PageShell from '../components/site/PageShell';
import PlatformDiagram from '../components/site/PlatformDiagram';
import { images } from '../config/esanzaContent';
import { positioning, ctas } from '../config/product';
import { itemVariants } from '../utils/animations';

const levels: Array<{
  id: string;
  audience: string;
  title: string;
  intro: string;
  can: string[];
  icon: React.ReactNode;
}> = [
  {
    id: 'farmers',
    audience: 'Farmers',
    title: 'Ask in your own words, from the field.',
    intro: 'Advice arrives with the farm, field and crop it belongs to — not as a generic answer.',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1.5M12 19.5V21M4.5 12H3m18 0h-1.5M6.4 6.4L5.35 5.35m13.3 13.3l-1.05-1.05M6.4 17.6l-1.05 1.05M18.65 5.35L17.6 6.4M9 12a3 3 0 116 0 3 3 0 01-6 0z" />
      </svg>
    ),
    can: [
      'Ask agricultural questions',
      'Type the question',
      'Speak the question',
      'Attach a photo where supported',
      'Receive context-aware guidance',
      'Receive field tasks from recommendations',
      'Get weather-sensitive advice',
    ],
  },
  {
    id: 'agronomists',
    audience: 'Agronomists / Extension Officers',
    title: 'Review before anything high-risk happens.',
    intro: 'Intelligence proposes; people decide. Every recommendation that matters passes through a review queue.',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    can: [
      'Review field risks',
      'Identify farms requiring attention',
      'Inspect recommendations and their sources',
      'Approve, modify or reject proposed work',
      'Review farmer observations',
      'Follow overdue work',
      'Monitor field history',
    ],
  },
  {
    id: 'institutions',
    audience: 'Organisations / Institutions',
    title: 'See the pattern across every farm.',
    intro: 'Aggregate operational views for programme managers, cooperatives and public institutions.',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
      </svg>
    ),
    can: [
      'See aggregate operational patterns',
      'Monitor farms across regions',
      'Identify geographic risks',
      'Track field action taken',
      'See verified reporting',
      'Understand production conditions',
    ],
  },
];

const pipeline = [
  'Mobile / Web',
  'eSanza Advisory Gateway',
  'Context Engine',
  'Knowledge / RAG',
  'AI Reasoning',
  'Agronomic Safety Rules',
  'Grounded Recommendation',
  'Farmer / Agronomist',
];

const FieldIntelligencePage: React.FC = () => (
  <>
    <SEO
      title="Field Intelligence | eSanza"
      description="Agricultural intelligence built into everyday field operations — context-aware advisory, weather and field risk with explainable recommendations and agronomist review."
      imagePath={images.tree}
      keywords="agricultural field intelligence, AI agronomy assistant, AI agriculture platform, precision agriculture, agricultural advisory"
      url="https://esanza.com/field-intelligence"
    />
    <PageShell>
      {/* ── Hero ─────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-[#06150e] px-5 pb-20 pt-32 lg:px-8">
        <img src={images.tree} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-[linear-gradient(100deg,#06150e_0%,rgba(6,21,15,.93)_45%,rgba(6,21,15,.5)_100%)]" />
        <div className="absolute -top-32 right-[-8%] h-[420px] w-[420px] rounded-full bg-[#d4af37]/20 blur-[130px]" />

        <div className="relative mx-auto max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#d4af37]/35 bg-[#d4af37]/10 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.24em] text-[#f3d978]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#d4af37] shadow-[0_0_12px_#d4af37]" />
              Field Intelligence
            </span>
            <h1 className="mt-7 text-5xl font-black leading-[1] tracking-tight text-white md:text-6xl">
              Agricultural intelligence built into{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f3d978] to-[#d4af37]">
                everyday field operations.
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-white/75">
              {positioning.ai} The same weather, crop stage, field history and observations the team already records are used to
              propose an action, explain it, and request human review when it matters.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={ctas.secondary.to} className="rounded-full bg-[#14532d] px-7 py-4 text-sm font-extrabold text-white shadow-xl shadow-[#14532d]/25 transition hover:-translate-y-0.5 hover:bg-[#184f2d]">
                {ctas.secondary.label}
              </Link>
              <Link to="/platform" className="rounded-full border border-white/18 px-7 py-4 text-sm font-extrabold text-white hover:bg-white/10">
                Explore the Platform
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Three levels ─────────────────────────────── */}
      <section className="bg-white px-5 py-24 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#987a1f]">Three levels</p>
            <h2 className="mt-4 text-4xl font-black tracking-tight text-[#102016] md:text-5xl">
              One intelligence layer. Three levels of use.
            </h2>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {levels.map((level, i) => (
              <motion.article
                key={level.id}
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-40px' }}
                className="flex flex-col rounded-2xl border border-[#dbe5d2] bg-[#f7f9f4] p-7 transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="flex items-center justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#14532d] text-[#f3d978]">
                    {level.icon}
                  </div>
                  <span className="text-xs font-black tracking-[0.2em] text-[#d4af37]">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <p className="mt-5 text-[11px] font-black uppercase tracking-[0.18em] text-[#987a1f]">{level.audience}</p>
                <h3 className="mt-2 text-xl font-black text-[#102016]">{level.title}</h3>
                <p className="mt-3 text-sm leading-6 text-[#596959]">{level.intro}</p>
                <ul className="mt-5 space-y-2 border-t border-[#eef2e8] pt-5">
                  {level.can.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-[#3d4a3d]">
                      <svg className="mt-0.5 h-4 w-4 shrink-0 text-[#14532d]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Explainability ───────────────────────────── */}
      <section className="bg-[#f7f9f4] px-5 py-24 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#987a1f]">Explainability</p>
            <h2 className="mt-4 text-4xl font-black tracking-tight text-[#102016] md:text-5xl">
              Every recommendation can show its work.
            </h2>
            <p className="mt-5 text-base leading-7 text-[#596959]">
              Intelligence output is never presented as unquestionable fact. Each recommendation can expose its risk level, the
              reasons behind it, the data it used, where that data came from, when it was captured, and whether a human has to
              review it first.
            </p>
            <div className="mt-6 rounded-xl border border-[#dbe5d2] bg-white p-5">
              <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#987a1f]">What is exposed</p>
              <ul className="mt-3 grid gap-2 text-sm text-[#3d4a3d] sm:grid-cols-2">
                {[
                  'Recommendation',
                  'Risk level',
                  'Why',
                  'Data used',
                  'Source',
                  'Timestamp',
                  'Confidence / uncertainty',
                  'Human review requirement',
                ].map((field) => (
                  <li key={field} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#d4af37]" aria-hidden="true" />
                    {field}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="rounded-2xl border border-[#dbe5d2] bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2 rounded-full border border-red-400/40 bg-red-50 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-red-700">
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008v.008H12v-.008z" />
                </svg>
                High risk
              </span>
              <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Example output</span>
            </div>
            <h3 className="mt-4 text-xl font-black text-[#102016]">Possible fungal disease risk</h3>

            <div className="mt-5 grid gap-4 text-sm">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#987a1f]">Why</p>
                <ul className="mt-2 space-y-1.5 text-[#3d4a3d]">
                  <li>• Recent rainfall</li>
                  <li>• High humidity</li>
                  <li>• Field observation yesterday</li>
                  <li>• Crop at fruiting stage</li>
                </ul>
              </div>
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#987a1f]">Recommended action</p>
                <p className="mt-2 text-[#3d4a3d]">Inspect the affected area before spraying.</p>
              </div>
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#987a1f]">Sources</p>
                <p className="mt-2 text-[#596959]">
                  Weather — 07 Oct 2026 06:00 · Field observation — 06 Oct 2026 · Crop stage — current record
                </p>
              </div>
              <div className="rounded-xl border border-[#d4af37]/40 bg-[#d4af37]/10 px-4 py-3 font-bold text-[#7a6212]">
                Human review: agronomist review recommended.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pipeline ─────────────────────────────────── */}
      <section className="bg-[#06150e] px-5 py-24 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#d4af37]">How it works</p>
            <h2 className="mt-4 text-4xl font-black tracking-tight text-white md:text-5xl">
              One advisory path. No second AI system.
            </h2>
            <p className="mt-5 text-base leading-7 text-white/60">
              Mobile and web reach the same advisory gateway. Context is assembled once, reasoning is grounded in agricultural
              knowledge, safety rules run before anything is shown, and high-risk output waits for a human.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-2.5">
            {pipeline.map((step, i) => (
              <React.Fragment key={step}>
                <span className="rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-sm font-bold text-white/85">
                  {step}
                </span>
                {i < pipeline.length - 1 && <span className="text-[#d4af37]" aria-hidden="true">→</span>}
              </React.Fragment>
            ))}
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {[
              {
                title: 'Human-in-the-loop',
                body: 'Chemical application, disease diagnosis, severe crop risk and high-cost interventions require agronomist approval before a task is created.',
              },
              {
                title: 'Offline-safe',
                body: 'When there is no connection the app says so plainly instead of dressing cached advice up as a live result.',
              },
              {
                title: 'Permissioned',
                body: 'Advisory access is scoped by tenant, role and feature flag — the same rules as the rest of the platform.',
              },
            ].map((card) => (
              <div key={card.title} className="rounded-2xl border border-white/10 bg-white/[0.06] p-6">
                <h3 className="text-base font-black text-white">{card.title}</h3>
                <p className="mt-2 text-sm leading-6 text-white/60">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Diagram + CTA ────────────────────────────── */}
      <section className="bg-[#0a1f14] px-5 py-24 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#d4af37]">In context</p>
            <h2 className="mt-4 text-4xl font-black tracking-tight text-white md:text-5xl">
              Intelligence sits in the middle of the system.
            </h2>
          </div>
          <PlatformDiagram className="mt-10" />
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to={ctas.secondary.to} className="rounded-full bg-[#14532d] px-8 py-4 text-sm font-extrabold text-white shadow-xl shadow-[#14532d]/25 transition hover:-translate-y-0.5 hover:bg-[#184f2d]">
              {ctas.secondary.label}
            </Link>
            <Link to="/contact" className="rounded-full border border-white/18 px-8 py-4 text-sm font-extrabold text-white hover:bg-white/10">
              Talk to eSanza
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  </>
);

export default FieldIntelligencePage;
